// Simple synchronous read-through cache to make in-app navigation feel instant.
// Populated by preloadUserData() right after login/session hydration.
// Consumers (Dashboard, Recipes, Stories) call getCached() first, paint
// immediately, then refresh in the background.

const store = { recipes: null, stories: null, families: null, ts: 0 };

export function getCachedList(key) {
  return store[key];
}

export function setCachedList(key, value) {
  store[key] = value;
  store.ts = Date.now();
}

export function clearCachedLists() {
  store.recipes = null; store.stories = null; store.families = null; store.ts = 0;
}

/**
 * Fires 3 parallel fetches in the background right after login. Also warms
 * the browser HTTP image cache by kicking off <img> preloads for the first
 * 12 cover images (enough to fill an above-the-fold Recipes page).
 * Safe to call multiple times — cheap.
 */
export async function preloadUserData(api) {
  try {
    const [recipes, stories, families] = await Promise.all([
      api.listRecipes().catch(() => []),
      api.listStories().catch(() => []),
      api.listFamilies().catch(() => []),
    ]);
    setCachedList('recipes', Array.isArray(recipes) ? recipes : []);
    setCachedList('stories', Array.isArray(stories) ? stories : []);
    setCachedList('families', Array.isArray(families) ? families : []);

    // Warm HTTP image cache — new Image() forces the browser to fetch each
    // URL. data:image/... URLs are inline, no fetch needed, so we skip those.
    const covers = [...(recipes || []), ...(stories || [])]
      .map(r => r?.cover)
      .filter(u => typeof u === 'string' && u && !u.startsWith('data:'))
      .slice(0, 12);
    for (const url of covers) {
      const img = new Image();
      img.decoding = 'async';
      img.loading = 'eager';
      img.src = url;
    }
  } catch { /* preload is best-effort */ }
}
